import { useEffect, useState } from 'react'
import type { AxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import { EntityKeys, QueryType } from 'utils/reactQuery'

import { logger } from 'utils/logging'
import { identifyMixpanelUser, registerMixpanelSuperProperties, unregisterMixpanelSuperProperty } from 'utils/analytics/mixpanel'
import { BootstrapMissingCause, rememberBootstrapLaunchAttemptId, reportPreScreenLoadFailure } from 'utils/analytics/orderFlowLoad'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'

import { readLaunchAttemptIdClaim } from './bootstrapTokenClaims'
import { takeBootstrapToken, wasBootstrapTokenTaken } from './bootstrapTokenHandoff'
import { embedSession } from './embedSession'
import { installEmbedSessionInterceptor } from './embedSessionInterceptor'

/**
 * Module-level single-flight latch for the boot exchange. A double effect-invoke (React
 * StrictMode, or a fast remount mid-exchange) would otherwise fire two `/exchange` calls and
 * burn the single-use bootstrap token twice. This dedupes only CONCURRENT in-flight boots: it
 * resets once the exchange settles, and reuse of a completed session is covered separately by
 * `embedSession.isActive()`.
 */
let bootInFlight = false

type EmbedSessionStatus = 'pending' | 'active' | 'error'

/** Refresh when the tab regains visibility — covers wake-from-sleep (overnight/weekend tabs). */
const refreshOnVisibility = () => {
  if (document.visibilityState === 'visible' && embedSession.isActive()) {
    embedSession.getAccessToken().catch(() => undefined)
  }
}

const bootstrapMissingCause = (params: ReturnType<typeof useBkbnSearchParams>): BootstrapMissingCause => {
  if (wasBootstrapTokenTaken()) return 'session_expired'
  if (params.get('redirect_status')) return 'stripe_return'
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  if (navigation?.type === 'reload' || navigation?.type === 'back_forward') return navigation.type
  return 'no_token'
}

/**
 * Boots the Propstack embed session: on the first mount it exchanges the single-use `bootstrapToken`
 * (moved out of the URL at startup by `captureBootstrapToken`) for the in-memory broker session, and installs
 * the 401 refresh-retry interceptor. A same-SPA re-mount (in-iframe navigation) reuses the live
 * session with no network call. Any exchange failure or a missing token resolves to `error`, which
 * the guard renders as the re-enter-from-Propstack screen — the burned token is never auto-retried.
 */
export const useEmbedSessionBootstrap = (): { status: EmbedSessionStatus } => {
  const params = useBkbnSearchParams()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<EmbedSessionStatus>(embedSession.isActive() ? 'active' : 'pending')

  useEffect(() => {
    installEmbedSessionInterceptor()
    document.addEventListener('visibilitychange', refreshOnVisibility)
    return () => document.removeEventListener('visibilitychange', refreshOnVisibility)
  }, [])

  useEffect(() => {
    if (embedSession.isActive()) return
    if (bootInFlight) return
    const bootstrapToken = takeBootstrapToken()
    if (!bootstrapToken) {
      const missingCause = bootstrapMissingCause(params)
      logger.warn('Embed boot without a bootstrap token', undefined, {
        context: { component: 'useEmbedSessionBootstrap', action: 'missingToken', metadata: { missingCause } },
      })
      reportPreScreenLoadFailure({ failureReason: 'BOOTSTRAP_MISSING', missingCause })
      setStatus('error')
      return
    }
    rememberBootstrapLaunchAttemptId(readLaunchAttemptIdClaim(bootstrapToken))

    bootInFlight = true
    embedSession.bootstrap(bootstrapToken)
      .then(() => {
        // Session-scoped Mixpanel dimensions (ISSUE-3213), known only now. Registered BEFORE going active:
        // the guarded children fire events from their own effects, which run before any ancestor's. Not in
        // embedSession itself, whose import graph must not reach the analytics layer (utils/auth cycle).
        // launch_attempt_id and workspace_type are snake_case on purpose: they must match the server events'
        // property names, or Mixpanel reports cannot join on them.
        const sessionDimensions = {
          isOneSystem: embedSession.getIsOneSystem(),
          workspaceId: embedSession.getWorkspaceId(),
          shopId: embedSession.getShopId(),
          launch_attempt_id: embedSession.getLaunchAttemptId(),
          workspace_type: embedSession.getWorkspaceType(),
        }
        registerMixpanelSuperProperties(sessionDimensions)
        // registerMixpanelSuperProperties drops a null rather than clearing the property, so a dimension this session
        // does not know would keep a previous session's value (same document, no reload). A wrong dimension is worse
        // than a missing one: clear every unknown one, as orderFlowProperty does.
        Object.entries(sessionDimensions)
          .filter(([, value]) => value === null)
          .forEach(([name]) => unregisterMixpanelSuperProperty(name))
        // Same timing for the broker's identity (ISSUE-4339): the server events carry their Auth0 id.
        const auth0UserId = embedSession.getAuth0UserId()
        if (auth0UserId) identifyMixpanelUser(auth0UserId)
        void queryClient.resetQueries({ queryKey: [EntityKeys.USER_ME, QueryType.GET], exact: true })
        setStatus('active')
      })
      .catch((error) => {
        // Log a REDACTED scalar status only — never the raw AxiosError, whose
        // `config.headers.Authorization` carries the bearer, and which the logger's 401 filter
        // would otherwise drop entirely (a burned/consumed bootstrap token 401s here).
        const status = (error as AxiosError)?.response?.status
        logger.warn('Embed bootstrap exchange failed', { status }, {
          context: { component: 'useEmbedSessionBootstrap', action: 'exchangeFailed' },
        })
        reportPreScreenLoadFailure({ failureReason: 'BOOTSTRAP_REJECTED', httpStatus: status ?? null })
        setStatus('error')
      })
      .finally(() => {
        bootInFlight = false
      })
    // Boot-time only: the embed entry freezes its params for the lifetime of the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { status }
}

/** Test-only: clears the boot single-flight latch so cases can be exercised in isolation. */
export const _resetBootLatchForTests = () => {
  bootInFlight = false
}
